import { message } from 'antd';
import { useHrLeaveStore } from '../stores/hr-leave-store';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { LeaveRequest, LeaveType, LeaveBalance } from '../types';

export function useLeave() {
  const store = useHrLeaveStore();

  const fetchLeaveTypes = async () => {
    try {
      const data = await customInstance<LeaveType[]>({ url: '/api/hr/leave-types', method: 'GET' });
      store.setLeaveTypes(data);
      return data;
    } catch {
      message.error('Failed to load leave types');
    }
  };

  const createLeaveType = async (dto: Partial<LeaveType>) => {
    try {
      await customInstance<LeaveType>({ url: '/api/hr/leave-types', method: 'POST', data: dto });
      message.success('Leave type created');
      await fetchLeaveTypes();
    } catch {
      message.error('Failed to create leave type');
    }
  };

  const updateLeaveType = async (id: number, dto: Partial<LeaveType>) => {
    try {
      await customInstance<LeaveType>({ url: `/api/hr/leave-types/${id}`, method: 'PUT', data: dto });
      message.success('Leave type updated');
      await fetchLeaveTypes();
    } catch {
      message.error('Failed to update leave type');
    }
  };

  const deleteLeaveType = async (id: number) => {
    try {
      await customInstance({ url: `/api/hr/leave-types/${id}`, method: 'DELETE' });
      message.success('Leave type deleted');
      await fetchLeaveTypes();
    } catch {
      message.error('Failed to delete leave type');
    }
  };

  const fetchLeaveRequests = async (page = 0, status = store.statusFilter) => {
    store.setLoading(true);
    try {
      const data = await customInstance<any>({
        url: `/api/hr/leave-requests?status=${status}&page=${page}&size=20`,
        method: 'GET',
      });
      store.setLeaveRequests(data.content ?? data, data.totalElements);
      return data;
    } catch {
      message.error('Failed to load leave requests');
    } finally {
      store.setLoading(false);
    }
  };

  const submitLeaveRequest = async (dto: Partial<LeaveRequest>) => {
    try {
      const data = await customInstance<LeaveRequest>({ url: '/api/hr/leave-requests', method: 'POST', data: dto });
      message.success('Leave request submitted');
      await fetchLeaveRequests();
      return data;
    } catch {
      message.error('Failed to submit leave request');
    }
  };

  const approveLeaveRequest = async (id: number, approverId: number) => {
    try {
      await customInstance({ url: `/api/hr/leave-requests/${id}/approve?approverId=${approverId}`, method: 'POST' });
      message.success('Leave request approved');
      await fetchLeaveRequests();
    } catch {
      message.error('Failed to approve leave request');
    }
  };

  const rejectLeaveRequest = async (id: number, approverId: number, reason: string) => {
    try {
      await customInstance({
        url: `/api/hr/leave-requests/${id}/reject?approverId=${approverId}`,
        method: 'POST',
        data: { reason },
      });
      message.success('Leave request rejected');
      await fetchLeaveRequests();
    } catch {
      message.error('Failed to reject leave request');
    }
  };

  const cancelLeaveRequest = async (id: number) => {
    try {
      await customInstance({ url: `/api/hr/leave-requests/${id}/cancel`, method: 'POST' });
      message.success('Leave request cancelled');
      await fetchLeaveRequests();
    } catch {
      message.error('Failed to cancel leave request');
    }
  };

  const fetchLeaveBalances = async (employeeId: number, year: number) => {
    try {
      const data = await customInstance<LeaveBalance[]>({
        url: `/api/hr/leave-balances/employee/${employeeId}?year=${year}`,
        method: 'GET',
      });
      store.setLeaveBalances(data);
      return data;
    } catch {
      message.error('Failed to load leave balances');
    }
  };

  const fetchPendingCount = async () => {
    try {
      const count = await customInstance<number>({ url: '/api/hr/leave-requests/pending/count', method: 'GET' });
      store.setPendingCount(count);
      return count;
    } catch {
      return 0;
    }
  };

  return {
    ...store,
    fetchLeaveTypes,
    createLeaveType,
    updateLeaveType,
    deleteLeaveType,
    fetchLeaveRequests,
    submitLeaveRequest,
    approveLeaveRequest,
    rejectLeaveRequest,
    cancelLeaveRequest,
    fetchLeaveBalances,
    fetchPendingCount,
  };
}
